class BuyLaterButton extends HTMLElement {
  #cookieKey = "buy_later";
  
  constructor() {
    super();

    this.addEventListener("click", this.handleClick);
  }

  handleClick(event) {
    event.preventDefault();

    // js-cookie が読み込めていない場合は保存できないため何もしない
    if (typeof Cookies === "undefined") return;

    const productId = this.dataset.productid;
    const variantId = this.dataset.variantid;
    const handle = this.dataset.handle;
    const quantity = this.dataset.quantity;
    
    const currentBuyLater = Cookies.get(this.#cookieKey);

    const date = new Date();
    const data = {
      productId: productId,
      variantId: variantId,
      handle: handle,
      quantity: quantity,
      timestamp: date.getTime(),
    };
    if (currentBuyLater) {
      const products = JSON.parse(currentBuyLater);
      if (!products.some((item) => item.productId === productId && item.variantId === variantId)) {
        const productsDateUpdate = products.map((item) => {
          return {
            ...item,
            timestamp: date.getTime(),
          }
        });
        this.setCookies(data, productsDateUpdate);
      } else {
        const filteredProducts = products.filter((item) => item.productId !== productId && item.variantId !== variantId);
        const filteredProductsDateUpdate = filteredProducts.map((item) => {
          return {
            ...item,
            timestamp: date.getTime(),
          }
        });
        this.setCookies(data, filteredProductsDateUpdate);
      }
    } else {
      this.setCookies(data);
    }

    const cartItems = this.closest("cart-items") || this.closest("cart-drawer-items");
    cartItems.updateQuantity(this.dataset.index, 0, undefined, undefined, false, () => {
      const buyLater = document.querySelector("buy-later");
      if (buyLater) {
        buyLater.initialize();
      }
    });

    try {
      if (typeof Shopify !== "undefined" && Shopify.analytics) {
        Shopify.analytics.publish("c_save_for_later_click", {
          product_id: productId,
          variant_id: variantId,
        });
        console.log("Shopify.analytics `c_save_for_later_click` sent");
      }
    } catch (err) {
      console.error("Tracking error:", err);
    }
  }

  /**
   * 指定したIDをクッキーに保存する
   *
   * @param {{ productId: string, variantId: string, handle: string, quantity: string }} item
   * @param {{ productId: string, variantId: string, handle: string, quantity: string }[]} items
   *
   * @returns void
   */
  setCookies(item, items = []) {
    Cookies.set(this.#cookieKey, JSON.stringify([item, ...items]), { expires: 365 });
  }
}
customElements.define("buy-later-button", BuyLaterButton);


class BuyLater extends HTMLElement {
  #cookieKey = "buy_later";
  #parsedData = undefined;
  #defaultHeadingText = window.buyLaterTranslateString.label;
  #defaultItemsText = window.buyLaterTranslateString.items;
  #durationText = window.buyLaterTranslateString.duration_text;
  #minute = window.buyLaterTranslateString.minute;
  #second = window.buyLaterTranslateString.second;
  #thresholdMinutes = 30 * 60 * 1000; // 30分
  // #thresholdMinutes = 90 * 1000;
  #intervalId = null;

  constructor() {
    super();

    this.initialize();
  }

  async initialize() {
    this.classList.remove("available");
    this.classList.remove("empty");
    this.classList.add("loading");

    if (this.#intervalId !== null) {
      clearInterval(this.#intervalId);
    }
    
    this.reloadCookie();
    
    if (this.#parsedData) {
      if (this.isExpired()) {
        this.setEmptyContent();
        this.addCartAllItems();
      } else {
        this.setProducts();
        this.timeCount();
      }
    } else {
      this.setEmptyContent()
    }
  }

  /**
   * クッキーに保存されている保存した日時で一番新しいものを返す
   * 
   * @returns number
   */
  getMaxTimestamp() {
    const timestamps = this.#parsedData?.map((data) => {
      return Number.isNaN(Number.parseInt(data.timestamp)) ? 0 : Number.parseInt(data.timestamp);
    }) ?? [0];

    return Math.max(...timestamps);
  }

  /**
   * 保存した日時から指定した閾値より過ぎているかどうかのチェック
   * 
   * @returns boolean
   */
  isExpired() {
    const maxTimestamp = this.getMaxTimestamp();
    const now = new Date();
    if (now.getTime() - maxTimestamp >= this.#thresholdMinutes) {
      // 閾値の時間が過ぎている
      return true;
    } else {
      return false;
    }
  }

  /**
   * 空であることを設定する
   */
  setEmptyContent() {
    this.classList.remove("available");
    this.classList.remove("loading");
    this.classList.add("empty");
    if (this.#defaultHeadingText) {
      // cloneNode 等による upgrade では heading が未複製の場合がある
      const heading = this.querySelector(".buy-later__heading");
      if (heading) heading.innerHTML = `${this.#defaultHeadingText}${this.#defaultItemsText.replace("__quantity__", "0")}`;
    }
  }

  async setProducts() {
    const container = this.querySelector(".product-tiles");
    if (!container) {
      return;
    }

    try {
      const localePaths = {
        "ja": "",
        "en": "/en",
        "zh-CN": "/zh-hans",
        "zh-TW": "/zh-hant",
        "ko": "/ko",
      };
      
      /** @type Array<string | null> */
      const responses = await Promise.all(this.#parsedData.map(async (value) => {
        const url = `//${window.location.host}${localePaths[Shopify.locale]}/products/${value.handle}?variant=${value.variantId}&section_id=buy-later-hidden`;
        const response = await fetch(url, {
          method: "GET",
          credentials: "include",
          headers: {
            "Content-type": "text/html",
          },
        });

        if (!response.ok) {
          return null;
        }

        return {
          data: await response.text(),
          info: value,
        };
      }));

      const parser = new DOMParser();
      const elements = responses.map((res) => {
        if (res.data) {
          const html = res.data;
          const parsedHtml = parser.parseFromString(html, "text/html");
          /** @type HTMLDivElement | null */
          const content = parsedHtml.querySelector(".buy-later-contents");
          if (content) {
            const li = document.createElement("li");

            const quantityContainer = content.querySelector(".buy-later-product__quantity span");
            if (quantityContainer) {
              quantityContainer.innerHTML = res.info.quantity;
            }

            content.querySelector(".buy-later-product__undo")?.addEventListener("click", () => {
              this.addCart([res.info], () => {
                window.location.reload();
              });
            });
            content.querySelector(".buy-later-product__remove")?.addEventListener("click", () => {
              this.removeCookie(res.info);
              this.initialize();
            });
            
            li.append(...content.children);
            return li;
          }

          return;
        }

        return;
      });

      container.innerHTML = "";
      container.append(...elements);

      if (this.#defaultHeadingText) {
        const quantity = this.#parsedData.reduce((current, value) => {
          return current + Number.parseInt(value.quantity);
        }, 0);
        const heading = this.querySelector(".buy-later__heading");
        if (heading) heading.innerHTML = `${this.#defaultHeadingText}${this.#defaultItemsText.replace("__quantity__", quantity.toString())}`;
      }

      this.classList.remove("empty");
      this.classList.remove("loading");
      this.classList.add("available");
    } catch (e) {
      // 描画失敗時は「あとで買う」一覧が静かに欠落するため記録する
      if (window.__themeSentry) {
        window.__themeSentry.withScope((scope) => {
          scope.setTag("cart_flow", "buy-later");
          scope.setContext("buy_later_render", { phase: "initialize" });
          window.__themeSentry.captureException(e);
        });
      }
      console.error(e);
    }
  }

  /**
   * 指定したIDをクッキーに保存する
   *
   * @param {{ productId: string, variantId: string, handle: string, quantity: string }} item
   * @param {{ productId: string, variantId: string, handle: string, quantity: string }[]} items
   *
   * @returns void
   */
  setCookies(data) {
    if (typeof Cookies === "undefined") return;
    Cookies.set(this.#cookieKey, JSON.stringify(data), { expires: 365 });

    this.reloadCookie();
  }

  /**
   * クッキーを削除する
   * 
   * @param {Object[] | Object} data 
   */
  removeCookie(data = undefined) {
    if (typeof Cookies === "undefined") return;
    if (data) {
      // ロジック統一のため必ず配列になるようにする
      const items = Array.isArray(data) ? data : [data];
      if (items.length === this.#parsedData?.length) {
        this.removeCookie();
      } else {
        const filteredProducts = this.#parsedData?.filter((product) => {
          const findItem = items.find((item) => {
            return item.productId === product.productId && item.variantId === product.variantId;
          });
  
          return !findItem;
        });
        this.setCookies(filteredProducts);
      }
    } else {
      Cookies.remove(this.#cookieKey);
    }

    this.reloadCookie();
  }


  /**
   * Classで持っているパースしたcookieを更新する
   */
  reloadCookie() {
    // js-cookie が読み込めていない場合は「保存なし」として空表示にフォールバック
    if (typeof Cookies === "undefined") {
      this.#parsedData = undefined;
      return;
    }
    const data = Cookies.get(this.#cookieKey);
    if (data) {
      this.#parsedData = JSON.parse(data);
    } else {
      this.#parsedData = undefined;
    }
  }

  /**
   * 現在時刻で商品の追加タイムスタンプを更新する
   */
  updateCookieTimestamp() {
    const date = new Date();

    this.setCookies(this.#parsedData.map((data) => {
      return {
        ...data,
        timestamp: date.getTime(),
      }
    }));
  }

  /**
   * 指定した商品群をカートに追加する
   * 在庫切れのものが入っている場合、在庫が残っているものも入らなくなるため一つずつAPIでカートに入れる
   * その際、alertを表示しつつ、最後のカートへの追加はカートのDOMを更新する
   * 
   * @param {Array} data 
   */
  addCart(data, callback) {
    fetch(`${window.Shopify.routes.root}cart/add.js`, {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-type": "application/json",
      },
      body: JSON.stringify(
        {
          items: data.map((item) => {
            return {
              id: item.variantId,
              quantity: item.quantity,
            }
          }),
        }
      ),
    })
    .then((response) => {
      return response.json();
    })
    .then((response) => {
      if ("items" in response) {
        this.removeCookie(data);

        const cartItems = document.querySelector("cart-items");
        if (cartItems) {
          if (cartItems.classList.contains("is-empty")) {
            cartItems.onCartUpdate();
          } else {
            cartItems.onCartUpdate();
          }
        }

        if (callback) {
          callback();
        }
      } else {
        // 再追加失敗。response.message が無いエラー(description のみ等)は
        // 従来ユーザーにも開発者にも完全に無音だったため、内容を必ず記録する
        if (window.__themeSentry) {
          let responseDetail;
          try {
            responseDetail = JSON.stringify(response).slice(0, 500);
          } catch (jsonError) {
            responseDetail = String(response);
          }
          window.__themeSentry.withScope((scope) => {
            scope.setTag("cart_flow", "buy-later");
            scope.setContext("buy_later_add", {
              requestedItems: data.map((item) => `${item.variantId}x${item.quantity}`).join(","),
              response: responseDetail,
              notifiedUser: Boolean(response.message),
            });
            window.__themeSentry.captureMessage("Buy-later cart add rejected", "error");
          });
        }
        if (response.message) {
          window.alert(`${response.message}\n該当の商品を削除して、再度お試しください`);
          this.updateCookieTimestamp();
          this.initialize();
        }
      }
    })
    .catch((e) => {
      if (window.__themeSentry) {
        window.__themeSentry.withScope((scope) => {
          scope.setTag("cart_flow", "buy-later");
          scope.setContext("buy_later_add", {
            requestedItems: data.map((item) => `${item.variantId}x${item.quantity}`).join(","),
          });
          window.__themeSentry.captureException(e);
        });
      }
      console.error(e);
    });
  }

  /**
   * クッキーに入っている商品をカートに追加する
   */
  addCartAllItems() {
    this.addCart(this.#parsedData, () => {
      window.location.reload();
    });
  }

  /**
   * クッキーに保存されている商品が後何分で削除される（カートに追加される）かどうかをカウントする
   * 1秒毎に更新するが、表示自体は「分」による表示
   */
  timeCount() {
    const timeContainer = this.querySelector(".buy-later__limit");
    const maxTimestamp = this.getMaxTimestamp();
    let duration = "";
    const timeSet = (millSecond) => {
      const second = millSecond / 1000;
      if (second > 60) {
        duration = `${(second / 60).toFixed(0)}${this.#minute}`;
      } else {
        duration = `${(second).toFixed(0)}${this.#second}`;
      }

      timeContainer.innerHTML = this.#durationText.replace("__duration__", `<em>${duration}</em>`);
    }

    if (timeContainer) {
      this.#intervalId = setInterval(() => {
        const diff = new Date().getTime() - maxTimestamp;
        const isOver = diff >= this.#thresholdMinutes;
        if (isOver) {
          timeSet(0);
          clearInterval(this.#intervalId);
          this.setEmptyContent();
          this.addCartAllItems();
        } else {
          timeSet(this.#thresholdMinutes - diff);
        }
      }, 1000);
    }
  }
}
customElements.define("buy-later", BuyLater);
